import { useEffect, useRef, useState } from "react";

/**
 * Returns a ref and a boolean indicating whether the element is in the viewport.
 * Once visible, stays true (one-shot reveal) unless `once` is false.
 *
 * Respects prefers-reduced-motion by marking visible immediately.
 *
 * @param {Object}  options
 * @param {string}  options.rootMargin  - IntersectionObserver rootMargin (default "0px 0px -60px 0px")
 * @param {number}  options.threshold   - visibility ratio to trigger (default 0.15)
 * @param {boolean} options.once        - stop observing after first intersection (default true)
 */
function useInView({ rootMargin = "0px 0px -60px 0px", threshold = 0.15, once = true } = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(node);
        }
      },
      { rootMargin, threshold },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [rootMargin, threshold, once]);

  return [ref, isVisible];
}

export default useInView;
